import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShoppingBag, FiSearch, FiArrowRight, FiCheck } = FiIcons;

const mockProducts = [
  {
    id: 1,
    name: 'Intraoral Scanner Pro X',
    category: 'Equipment',
    image: 'https://images.unsplash.com/photo-1618498213044-12d1d82173d4?w=600&h=400&fit=crop',
    description: 'High-precision intraoral scanner for digital impressions. Features AI-driven scanning and cloud integration.',
    recommendation: [
      'Increases patient comfort and reduces chair time.',
      'Improves accuracy over traditional impressions.',
      'Seamless integration with CAD/CAM systems.',
    ],
    link: '#',
  },
  {
    id: 2,
    name: 'DentalPractice OS',
    category: 'Software',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    description: 'All-in-one practice management software. Manages scheduling, billing, patient records, and marketing.',
    recommendation: [
      'Streamlines administrative tasks, saving hours per week.',
      'Enhances patient communication with automated reminders.',
      'Provides robust reporting for business intelligence.',
    ],
    link: '#',
  },
  {
    id: 3,
    name: 'The Art of Case Presentation',
    category: 'Books',
    image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600&h=400&fit=crop',
    description: 'A comprehensive guide to effectively communicating treatment plans and increasing case acceptance.',
    recommendation: [
      'Provides actionable scripts and techniques.',
      'Based on psychological principles of persuasion.',
      'Includes digital and in-person presentation strategies.',
    ],
    link: '#',
  },
  {
    id: 4,
    name: 'Surgical Loupes & Light System',
    category: 'Equipment',
    image: 'https://images.unsplash.com/photo-1628258334105-2a0b3d6ef513?w=600&h=400&fit=crop',
    description: 'Ergonomic surgical loupes with a powerful LED light system for enhanced visibility and precision.',
    recommendation: [
      'Reduces eye strain and improves posture.',
      'Magnification improves diagnostic accuracy.',
      'Lightweight and comfortable for all-day use.',
    ],
    link: '#',
  },
];

const categories = ['All', 'Equipment', 'Software', 'Books'];

const ProductCard = ({ product }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.3 }}
    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
  >
    <img src={product.image} alt={product.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform" />
    <div className="p-6">
      <span className="text-xs font-semibold uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{product.category}</span>
      <h3 className="text-xl font-bold text-gray-900 mt-3 mb-2">{product.name}</h3>
      <p className="text-sm text-gray-600 mb-4 h-16">{product.description}</p>
      
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Why we recommend it:</h4>
        <ul className="space-y-1.5">
          {product.recommendation.map((point, i) => (
            <li key={i} className="flex items-start">
              <SafeIcon icon={FiCheck} className="flex-shrink-0 w-4 h-4 text-green-500 mt-0.5 mr-2" />
              <span className="text-xs text-gray-600">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      <a
        href={product.link}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full mt-4 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        Learn More <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
      </a>
    </div>
  </motion.div>
);

const ProductRecommendations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts = useMemo(() => {
    return mockProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, activeCategory]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="p-8 bg-gray-50 min-h-full"
    >
      <div className="flex items-center space-x-4 mb-8">
        <div className="p-3 bg-blue-100 rounded-full">
          <SafeIcon icon={FiShoppingBag} className="h-8 w-8 text-blue-600" />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-gray-800">Product Recommendations</h1>
          <p className="text-lg text-gray-500">Curated products and services to enhance your practice.</p>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-auto">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-64 bg-gray-100 border-transparent rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProducts.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </motion.div>
  );
};

export default ProductRecommendations;